import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import styled from "@emotion/styled";
import { Box, Button, Grid, TextField } from "@mui/material";
import { FC } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import FinancialDataInputForm from './FinancialDataInputForm';
import SelectIndustryModal from '../selectIndustry/SelectIndustryModal';
import swal from 'sweetalert2';
import React from 'react';
import { industryDataInit } from 'pages/selectIndustry/IndustryData';
import { sessionDisconnectedCheck } from 'shared/sessionDisconnectedCheck';

const HeaderLabel = styled.header`
    background-color: #41DBCE;
    color: #FFFFFF;
    height: 3rem;
    font-size: 1.5rem;
    padding-top: 0.5rem;
    padding-left: 4rem;
`;

const BodyTextDiv = styled.div`
    text-align: center;
    margin-top: 1.5rem;
    margin-bottom:1.5rem;
`;

const IndustryDesignationGrid = styled(Grid)(({ }) => ({
    backgroundColor: '#F2F2F2',
    textAlign: 'center',
    fontSize: '1.5rem',
    height: '18rem',
    lineHeight: '2.5rem',
}));

const StandardButton = styled(Button)(({ }) => ({
    backgroundColor: '#01CFBE',
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    height: '3.2rem',
    width: '30rem',
    borderRadius: '100rem',
}));

const TextFieldBox = styled(Box)(({ }) => ({
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#595959',
}));

const TextFieldLabel = styled(Grid)(({ }) => ({
    backgroundColor: '#595959',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const TextFieldForm = styled(Grid)(({ }) => ({
    backgroundColor: '#FFFFFF',
}));

const DataInputForm = styled(TextField)(({ }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            border: 'none',
        },
        '&:hover fieldset': {
            border: 'none',
        },
        '&.Mui-focused fieldset': {
            border: 'none',
        },
    },
    "& .MuiInputBase-input": {
        fontSize: "0.92rem",
        marginTop: "0.08rem"
    },
    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000", },
}))

const AnalysisButtonArea = styled(Box)(({ }) => ({
    backgroundColor: '#595959',
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '1rem',
    height: '9rem',
    lineHeight: '4rem',
    marginTop: '0.5rem',
    marginBottom: '3rem'
}));

const AnalysisButton = styled(Button)(({ }) => ({
    backgroundColor: '#F2F2F2',
    color: '#595959',
    textAlign: 'left',
    fontSize: '1.2rem',
    height: '3.2rem',
    width: '17rem',
    borderRadius: '100rem',
}));

const buttonStyle = { marginTop: '25px' };

const screenId = "U040"

const FinancialAnalysis: FC = () => {
    const navigate = useNavigate();
    const location = useLocation()
    const [isSelectIndustryOpen, setIsSelectIndustryOpen] = React.useState<boolean>(false)
    const [industryData, setIndustryData] = React.useState<any>(industryDataInit)
    const [yearEdition] = React.useState<string>(location.state.yearEdition)
    const [inputState, setInputState] = React.useState(location.state.inputState || undefined);

    React.useEffect(() => {
        const interval = setInterval(async () => {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "polling", "", navigate)) {
                return;
            }
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    React.useEffect(() => {
        if (location.state != undefined) {
            if (location.state.industryData != undefined) {
                setIndustryData(location.state.industryData)
            }
        }
    }, [])

    const handleConfirm = (industryData: any) => {
        setIndustryData(industryData)
        setIsSelectIndustryOpen(false)
    }

    const onClickAnalysis = async () => {
        // 入力値チェック
        const tableValue = inputState["tableValue"]
        if (industryData.industryName == "") {
            // 業種が未選択
            swal.fire({
                text: '業種を選択してください',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else if (checkFiscalYearEmpty(tableValue)) {
            // 決算年のみ未入力の決算期がある
            swal.fire({
                text: '年が入力されていない列が存在します',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else if (checkFiscalMonthEmpty(tableValue)) {
            // 決算月のみ未入力の決算期がある
            swal.fire({
                text: '月が入力されていない列が存在します',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else if (checkFiscalMonthRange(tableValue)) {
            // 決算月に1～12の以外の値が存在
            swal.fire({
                text: '月には1-12の数値を入力してください',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else if (checkFiscalDuplicate(tableValue)) {
            // 決算期の値が重複
            swal.fire({
                text: '同じ決算期が入力されています',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else if (checkFiscalEmpty(tableValue)) {
            // ラジオボタンで選択している決算期が未入力
            swal.fire({
                text: '財務分析対象の決算期が未入力です',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else if (checkFiscalMonthEmptyAndInput(tableValue)) {
            // 決算年月が未入力の列に入力がある
            swal.fire({
                text: 'データ入力された列に期が設定されていません',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else {
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "onClickAnalysis", yearEdition, navigate)) {
                return
            }

            // 「-」入力からフォーカスアウトアウト前に財務分析ボタンを押下される場合ががるため、「-」が設定されていたら0に置換する
            for (const key in tableValue["fiscalYM1"]["BS"]) {
                tableValue["fiscalYM1"]["BS"][key] = tableValue["fiscalYM1"]["BS"][key] === "-" ? 0 : tableValue["fiscalYM1"]["BS"][key]
            }
            for (const key in tableValue["fiscalYM2"]["BS"]) {
                tableValue["fiscalYM2"]["BS"][key] = tableValue["fiscalYM2"]["BS"][key] === "-" ? 0 : tableValue["fiscalYM2"]["BS"][key]
            }
            for (const key in tableValue["fiscalYM3"]["BS"]) {
                tableValue["fiscalYM3"]["BS"][key] = tableValue["fiscalYM3"]["BS"][key] === "-" ? 0 : tableValue["fiscalYM3"]["BS"][key]
            }
            for (const key in tableValue["fiscalYM1"]["PL"]) {
                tableValue["fiscalYM1"]["PL"][key] = tableValue["fiscalYM1"]["PL"][key] === "-" ? 0 : tableValue["fiscalYM1"]["PL"][key]
            }
            for (const key in tableValue["fiscalYM2"]["PL"]) {
                tableValue["fiscalYM2"]["PL"][key] = tableValue["fiscalYM2"]["PL"][key] === "-" ? 0 : tableValue["fiscalYM2"]["PL"][key]
            }
            for (const key in tableValue["fiscalYM3"]["PL"]) {
                tableValue["fiscalYM3"]["PL"][key] = tableValue["fiscalYM3"]["PL"][key] === "-" ? 0 : tableValue["fiscalYM3"]["PL"][key]
            }
            navigate("/FinancialAnalysis/Result", { state: { inputState, industryData, yearEdition } });
        }
    };

    // 決算年未入力チェック（決算月のみ入力されていて決算年が未入力の列があればtrue）
    const checkFiscalYearEmpty = ((tableValue: { [x: string]: any }) => {
        if (tableValue["fiscalYM1"]["fiscalYear"] == "" && tableValue["fiscalYM1"]["fiscalMonth"] != ""
            || tableValue["fiscalYM2"]["fiscalYear"] == "" && tableValue["fiscalYM2"]["fiscalMonth"] != ""
            || tableValue["fiscalYM3"]["fiscalYear"] == "" && tableValue["fiscalYM3"]["fiscalMonth"] != ""
        ) {
            return true
        } else {
            return false
        }
    })

    // 決算月未入力チェック（決算年のみ入力されていて決算月が未入力の列があればtrue）
    const checkFiscalMonthEmpty = ((tableValue: { [x: string]: any }) => {
        if (tableValue["fiscalYM1"]["fiscalYear"] != "" && tableValue["fiscalYM1"]["fiscalMonth"] == ""
            || tableValue["fiscalYM2"]["fiscalYear"] != "" && tableValue["fiscalYM2"]["fiscalMonth"] == ""
            || tableValue["fiscalYM3"]["fiscalYear"] != "" && tableValue["fiscalYM3"]["fiscalMonth"] == ""
        ) {
            return true
        } else {
            return false
        }
    })

    // 決算月未入力かつ明細入力チェック（決算年もしくは決算月が未入力の列に入力があればtrue）
    const checkFiscalMonthEmptyAndInput = ((tableValue: { [x: string]: any }) => {
        var ret = false;
        var target = [];
        // 決算年もしくは決算月が未入力の列の特定
        if (tableValue["fiscalYM1"]["fiscalYear"] == "" || tableValue["fiscalYM1"]["fiscalMonth"] == "") target.push("fiscalYM1");
        if (tableValue["fiscalYM2"]["fiscalYear"] == "" || tableValue["fiscalYM2"]["fiscalMonth"] == "") target.push("fiscalYM2");
        if (tableValue["fiscalYM3"]["fiscalYear"] == "" || tableValue["fiscalYM3"]["fiscalMonth"] == "") target.push("fiscalYM3");
        if (target.indexOf("fiscalYM1") > -1) {
            for (const key in tableValue["fiscalYM1"]["BS"]) {
                if (String(tableValue["fiscalYM1"]["BS"][key]) != "0") ret = true;
            }
            for (const key in tableValue["fiscalYM1"]["PL"]) {
                if (String(tableValue["fiscalYM1"]["PL"][key]) != "0") ret = true;
            }
        }
        if (target.indexOf("fiscalYM2") > -1) {
            for (const key in tableValue["fiscalYM2"]["BS"]) {
                if (String(tableValue["fiscalYM2"]["BS"][key]) != "0") ret = true;
            }
            for (const key in tableValue["fiscalYM2"]["PL"]) {
                if (String(tableValue["fiscalYM2"]["PL"][key]) != "0") ret = true;
            }
        }
        if (target.indexOf("fiscalYM3") > -1) {
            for (const key in tableValue["fiscalYM3"]["BS"]) {
                if (String(tableValue["fiscalYM3"]["BS"][key]) != "0") ret = true;
            }
            for (const key in tableValue["fiscalYM3"]["PL"]) {
                if (String(tableValue["fiscalYM3"]["PL"][key]) != "0") ret = true;
            }
        }
        return ret;
    })

    // 決算月範囲チェック（決算月の入力値が1～12の範囲外ならtrue）
    const checkFiscalMonthRange = ((tableValue: { [x: string]: any }) => {
        if ((tableValue["fiscalYM1"]["fiscalMonth"] != "" && !(1 <= Number(tableValue["fiscalYM1"]["fiscalMonth"]) && Number(tableValue["fiscalYM1"]["fiscalMonth"]) <= 12))
            || (tableValue["fiscalYM2"]["fiscalMonth"] != "" && !(1 <= Number(tableValue["fiscalYM2"]["fiscalMonth"]) && Number(tableValue["fiscalYM2"]["fiscalMonth"]) <= 12))
            || (tableValue["fiscalYM3"]["fiscalMonth"] != "" && !(1 <= Number(tableValue["fiscalYM3"]["fiscalMonth"]) && Number(tableValue["fiscalYM3"]["fiscalMonth"]) <= 12))
        ) {
            return true
        } else {
            return false
        }
    })

    // 決算期重複チェック（入力済みの決算期が重複していればtrue）
    const checkFiscalDuplicate = ((tableValue: { [x: string]: any }) => {
        let fiscalYM1 = tableValue["fiscalYM1"]["fiscalYear"] + tableValue["fiscalYM1"]["fiscalMonth"]
        let fiscalYM2 = tableValue["fiscalYM2"]["fiscalYear"] + tableValue["fiscalYM2"]["fiscalMonth"]
        let fiscalYM3 = tableValue["fiscalYM3"]["fiscalYear"] + tableValue["fiscalYM3"]["fiscalMonth"]
        if ((fiscalYM1 == fiscalYM2 && fiscalYM1 + fiscalYM2 != "")
            || (fiscalYM2 == fiscalYM3 && fiscalYM2 + fiscalYM2 != "")
            || (fiscalYM1 == fiscalYM3 && fiscalYM1 + fiscalYM3 != "")
        ) {
            return true
        } else {
            return false
        }
    })

    // 決算期未入力チェック（ラジオボタンで選択済みの決算期が未入力の場合true）
    const checkFiscalEmpty = ((tableValue: { [x: string]: any }) => {
        let isComparisonTarget1 = tableValue["fiscalYM1"]["isComparisonTarget"]
        let isComparisonTarget2 = tableValue["fiscalYM2"]["isComparisonTarget"]
        let isComparisonTarget3 = tableValue["fiscalYM3"]["isComparisonTarget"]
        let fiscalYM = ""
        if (isComparisonTarget1) {
            fiscalYM = tableValue["fiscalYM1"]["fiscalMonth"] + tableValue["fiscalYM1"]["fiscalMonth"]
        } else if (isComparisonTarget2) {
            fiscalYM = tableValue["fiscalYM2"]["fiscalMonth"] + tableValue["fiscalYM2"]["fiscalMonth"]
        } else if (isComparisonTarget3) {
            fiscalYM = tableValue["fiscalYM3"]["fiscalMonth"] + tableValue["fiscalYM3"]["fiscalMonth"]
        }
        if (fiscalYM == "") {
            return true
        } else {
            return false
        }
    })

    const onClickTopPage = async () => {
        // セッションの有効性確認
        if (await sessionDisconnectedCheck(screenId, "onClickTopPage", yearEdition, navigate)) {
            return
        }
        navigate("/Top");
    };

    return (
        <>
            <HeaderLabel>
                財務分析
            </HeaderLabel>
            <body>
                <BodyTextDiv>
                    分類種別・業種名を指定し、貸借対照表または損益計算書を入力して「分析」ボタンをクリックしてください。
                </BodyTextDiv>
                <IndustryDesignationGrid>
                    <div style={{ fontWeight: 'bold', padding: '1rem', color: '#00A99C', }}>
                        比較業種
                    </div>
                    <div>
                        <StandardButton variant="contained" color="secondary" onClick={() => setIsSelectIndustryOpen(true)}>分類種別・業種名を指定</StandardButton>
                    </div>
                    <ArrowDropDownIcon style={{ color: '#BFBFBF', fontSize: '5rem', margin: '-1rem' }} />
                    <Grid container spacing={4}>
                        <Grid size={1}></Grid>
                        <Grid size={4}>
                            <TextFieldBox border={1}>
                                <Grid container>
                                    <TextFieldLabel size={4}>分類種別</TextFieldLabel>
                                    <TextFieldForm size={8}>
                                        <DataInputForm placeholder="上のボタンから指定してください" value={industryData.classification} fullWidth disabled variant='outlined' size='small' />
                                    </TextFieldForm>
                                </Grid>
                            </TextFieldBox>
                        </Grid>
                        <Grid size={6}>
                            <TextFieldBox border={1}>
                                <Grid container>
                                    <TextFieldLabel size={3}>業種名</TextFieldLabel>
                                    <TextFieldForm size={9}>
                                        <DataInputForm placeholder="上のボタンから指定してください" value={industryData.industryName} fullWidth disabled variant='outlined' size='small' />
                                    </TextFieldForm>
                                </Grid>
                            </TextFieldBox>
                        </Grid>
                        <Grid size={1}></Grid>
                    </Grid>
                    <Button sx={{ marginTop: '0.4rem', color: '#696969', fontSize: '1.2rem' }} onClick={() => { setIndustryData(industryDataInit) }}>
                        <a>クリア</a>
                    </Button>
                </IndustryDesignationGrid>
                &nbsp;
                <FinancialDataInputForm inputState={inputState} yearEdition={yearEdition} setState={setInputState} />
                <AnalysisButtonArea>
                    <AnalysisButton variant="contained" color="secondary" style={buttonStyle} onClick={onClickAnalysis}><img src={`${process.env.PUBLIC_URL}/Icons/analyzeIcon.svg`} width="30px" />&nbsp;分析</AnalysisButton>
                    <div>
                        <span onClick={onClickTopPage} style={{ cursor: 'pointer' }}>
                            <img src={`${process.env.PUBLIC_URL}/Icons/returnIcon.svg`} width="13px" />&nbsp;&nbsp;戻る
                        </span>
                    </div>
                </AnalysisButtonArea>
                <SelectIndustryModal industryData={industryData} isOpen={isSelectIndustryOpen} handleCloseModal={() => setIsSelectIndustryOpen(false)} yearEdition={yearEdition} onConfirm={handleConfirm} />
            </body>
        </>
    );
}

export default FinancialAnalysis;
